import React from 'react';
import { cn } from '../lib/utils';
import { useTheme } from '../config/theme';
import { accentButtonStyle } from './accentButton';

const Button = React.forwardRef(({
  children,
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  fullWidth = false,
  className,
  style,
  ...props
}, ref) => {
  const theme = useTheme();
  // Mobile-first: ensure tap targets are >=44px on small screens.
  const baseClasses = 'inline-flex items-center justify-center font-medium rounded-lg transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[color:var(--brand)]/40 ring-offset-transparent disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation select-none max-sm:min-h-11 max-sm:min-w-11';

  const variants = {
    primary: 'accent-button',
    secondary: 'accent-button accent-button--alt',
    accent: 'accent-button accent-button--alt',
    outline: 'border',
    ghost: '',
    danger: '',
    success: '',
  };
  
  const sizes = {
    sm: 'px-3 py-2 text-sm',
    md: 'px-4 py-2.5 text-sm',
    lg: 'px-6 py-3 text-base',
    xl: 'px-8 py-4 text-lg',
  };
  
  const classes = cn(
    baseClasses,
    variants[variant],
    sizes[size],
    fullWidth && 'w-full',
    className
  );

  const styleByVariant = (() => {
    switch (variant) {
      case 'primary':
        return {
          ...accentButtonStyle,
          boxShadow: '0 4px 14px rgba(var(--brand-rgb), .10)',
          transition: 'transform .12s ease, box-shadow .2s ease, background .2s ease',
        };
      case 'secondary':
      case 'accent':
        return { ...accentButtonStyle, boxShadow: '0 4px 14px rgba(var(--brand-alt-rgb), .10)' };
      case 'outline':
        return { background: theme.mode === 'light' ? 'rgba(255,255,255,.6)' : 'rgba(255,255,255,.04)', color: theme.text, borderColor: theme.border, backdropFilter: `saturate(140%) blur(${theme.glassBlur || '10px'})` };
      case 'ghost':
        return { background: theme.glassBg, color: theme.text, borderColor: theme.glassBorder, backdropFilter: `saturate(140%) blur(${theme.glassBlur || '10px'})` };
      case 'danger':
        return { background: theme.danger, color: '#fff', borderColor: 'transparent', boxShadow: theme.shadow };
      case 'success':
        return { background: '#10b981', color: '#fff', borderColor: 'transparent', boxShadow: theme.shadow };
      default:
        return {};
    }
  })();
  
  return (
    <button
      ref={ref}
      className={classes}
      style={{ ...styleByVariant, ...style }}
      onMouseDown={(e)=>{ try { e.currentTarget.style.transform='scale(.985)'; } catch {} }}
      onMouseUp={(e)=>{ try { e.currentTarget.style.transform='scale(1)'; } catch {} }}
      onMouseLeave={(e)=>{ try { e.currentTarget.style.transform='scale(1)'; } catch {} }}
      disabled={disabled || loading}
      {...props}
      aria-busy={loading}
      aria-disabled={disabled || loading}
    >
      {loading && (
        <svg
          className="animate-spin -ml-1 mr-2 h-4 w-4"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      )}
      {children}
    </button>
  );
});

Button.displayName = 'Button';

export default Button;
